/**
 * An administrator confirming that the licence a nurse still finishing her
 * registration saved is hers - POST /users/{id}/licence-confirmations
 * (decision D3; pacmny-api NurseLicenceOwnership).
 *
 * Her onboarding completes only when the licence she typed is proven hers:
 * NCSBN accepted her own enrolment of it, or an administrator said so. Saving
 * her nurse card is NOT saying so - the card sends back the licence number it
 * shows on every save, so fixing her phone number would vouch for whatever
 * she typed. This is the deliberate act instead: its own control on her card,
 * its own question, its own request, naming the number and state the
 * administrator was shown (the API refuses it, 409 `licence_changed`, if she
 * has changed them since).
 */

import api from './client';

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

/** The licence a staff row (bridgeMap staffRows) shows: number and state. */
function shownLicence(row) {
  return { number: text(row && row.licensenum), state: text(row && row.licensestate).toUpperCase() };
}

/**
 * Whether her card offers the confirmation: only while she has not finished
 * her registration (`needs_onboarding` from GET /users) and has a licence
 * number and state to confirm.
 */
export function offersLicenceConfirmation(row) {
  const licence = shownLicence(row);

  return Boolean(row && row.needs_onboarding === true && licence.number !== '' && licence.state !== '');
}

/** The question asked before anything is sent: whose licence, which one. */
export function licenceConfirmationQuestion(row) {
  const licence = shownLicence(row);
  const name = [text(row && row.firstname), text(row && row.lastname)].filter(Boolean).join(' ') || 'this nurse';

  return `Confirm that ${licence.state} licence ${licence.number} belongs to ${name}?\n\n`
    + 'She cannot finish her registration until her licence is shown to be hers. '
    + 'Only confirm it if you have checked that it is - for example against her licence document. '
    + 'Your confirmation is recorded.';
}

export const LICENCE_CONFIRMED = 'Licence confirmed. She can now finish her registration.';

/**
 * What to tell the administrator when the API refused: its own sentence for
 * a refusal it explains (her licence changed, nothing left to confirm, no
 * licence on file, no right to do this), and a plain retry otherwise.
 */
export function licenceConfirmationRefusal(error) {
  const status = Number(error && error.status);

  if (status && status < 500 && status !== 429 && error.message) return error.message;

  return 'The licence could not be confirmed just now. Please try again.';
}

/** Resolves to the confirmation (`user_id`, `licence_ownership`, ...); rejects with an ApiError. */
export function confirmNurseLicence(row) {
  const licence = shownLicence(row);
  const userId = text(row && (row.unique_ID ?? row.providerid ?? row.pmid));

  return api.post(`/users/${encodeURIComponent(userId)}/licence-confirmations`, {
    license_number: licence.number,
    license_state: licence.state,
  });
}

export default {
  offersLicenceConfirmation,
  licenceConfirmationQuestion,
  licenceConfirmationRefusal,
  confirmNurseLicence,
};
